import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// ─── Corporate Identity ───────────────────────────────────────────────────────
const C = {
  black:      [10, 10, 10],
  darkBg:     [17, 17, 17],
  cardBg:     [248, 248, 248],
  cardBg2:    [240, 240, 240],
  green:      [58, 127, 107],
  greenLight: [76, 175, 133],
  greenBg:    [232, 240, 236],
  // ERP accent: #D6EF4E. Keep the legacy key names so the PDF layout
  // stays stable while the visual identity matches the application.
  gold:       [214, 239, 78],
  goldLight:  [226, 244, 132],
  white:      [255, 255, 255],
  offWhite:   [250, 249, 246],
  gray:       [130, 130, 130],
  grayLight:  [180, 180, 180],
  grayDark:   [80, 80, 80],
  grayBorder: [220, 220, 220],
  red:        [200, 60, 60],
  redBg:      [252, 228, 228],
  yellow:     [180, 140, 20],
  yellowBg:   [252, 244, 215],
  rowGreen:   [220, 242, 234],
  rowYellow:  [252, 244, 215],
  rowRed:     [252, 228, 228],
  textDark:   [30, 30, 30],
  textMid:    [70, 70, 70],
};

const COMPANY = {
  name:    'Las Dos Doncellas',
  tagline: 'Productos Ibéricos',
  web:     'www.lasdosdoncellas.es',
};

// ─── Page dimensions (A4 Portrait) ───────────────────────────────────────────
const PW = 210;  // portrait width
const PH = 297;  // portrait height
const M  = 12;   // margin

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmt = (v, dec = 2) => {
  const n = parseFloat(v);
  return isNaN(n) ? '-' : n.toFixed(dec);
};

const fmtDate = (d) => {
  if (!d) return '-';
  try { return new Date(d).toLocaleDateString('es-ES', { year: 'numeric', month: '2-digit', day: '2-digit' }); }
  catch { return '-'; }
};

const getWeekNumber = (d) => {
  const date = new Date(d);
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() + 3 - ((date.getDay() + 6) % 7));
  const week1 = new Date(date.getFullYear(), 0, 4);
  return 1 + Math.round(((date.getTime() - week1.getTime()) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7);
};

const rendColor = (r) => {
  const v = parseFloat(r);
  if (isNaN(v)) return C.gray;
  if (v >= 45) return C.green;
  if (v >= 40) return C.yellow;
  return C.red;
};

const rendBgColor = (r) => {
  const v = parseFloat(r);
  if (isNaN(v)) return C.cardBg2;
  if (v >= 45) return C.greenBg;
  if (v >= 40) return C.yellowBg;
  return C.redBg;
};

const rendLabel = (r) => {
  const v = parseFloat(r);
  if (isNaN(v)) return '-';
  if (v > 50) return 'Excelente';
  if (v >= 45) return 'Bueno';
  if (v >= 40) return 'Revisar';
  return 'Bajo';
};

const rendEmoji = (r) => {
  const v = parseFloat(r);
  if (isNaN(v)) return '';
  if (v >= 45) return '🟢';
  if (v >= 40) return '🟡';
  return '🔴';
};

const normalizeFotoUrls = (value) => {
  if (!value) return [];
  let raw = value;
  if (typeof value === 'string') {
    try { raw = JSON.parse(value); } catch { raw = value; }
  }
  const list = Array.isArray(raw) ? raw : [raw];
  return [...new Set(list.map(item => typeof item === 'string' ? item : item?.url).filter(Boolean))];
};

// ─── Logo loader ──────────────────────────────────────────────────────────────
const loadLogo = () =>
  new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const c = document.createElement('canvas');
      c.width = img.width; c.height = img.height;
      c.getContext('2d').drawImage(img, 0, 0);
      resolve(c.toDataURL('image/png'));
    };
    img.onerror = () => resolve(null);
    img.src = '/assets/images/logo-las-dos-doncellas..jpg';
  });

const loadImageFromUrl = (url) =>
  new Promise((resolve) => {
    if (!url) return resolve(null);
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const c = document.createElement('canvas');
        c.width = img.width; c.height = img.height;
        c.getContext('2d').drawImage(img, 0, 0);
        resolve(c.toDataURL('image/jpeg'));
      } catch { resolve(null); }
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });

// ─── Drawing primitives ───────────────────────────────────────────────────────
const fillRect = (doc, x, y, w, h, color) => {
  doc.setFillColor(...color);
  doc.rect(x, y, w, h, 'F');
};

const strokeRect = (doc, x, y, w, h, color, lw = 0.3) => {
  doc.setDrawColor(...color);
  doc.setLineWidth(lw);
  doc.rect(x, y, w, h, 'S');
};

const txt = (doc, str, x, y, opts = {}) => {
  doc.text(String(str ?? ''), x, y, opts);
};

// ─── Page Header (portrait) ───────────────────────────────────────────────────
const drawPageHeader = (doc, logo, clienteName, periodo, pageNum, totalPages) => {
  // ERP lime accent bar
  fillRect(doc, 0, 0, PW, 1.5, C.gold);
  // White header area
  fillRect(doc, 0, 1.5, PW, 22, C.white);
  // Bottom border
  fillRect(doc, 0, 23, PW, 0.4, C.grayBorder);

  // Logo
  if (logo) {
    try { doc.addImage(logo, 'PNG', M, 3, 16, 16); } catch {}
  }

  // Company / document identity — same hierarchy as the ERP.
  doc.setFontSize(7);
  doc.setFont('times', 'bold');
  doc.setTextColor(...C.gold);
  txt(doc, COMPANY.name.toUpperCase(), M + 20, 8);

  doc.setFontSize(10.5);
  doc.setFont('times', 'bold');
  doc.setTextColor(...C.textDark);
  txt(doc, 'INFORME DE PRODUCCIÓN Y LONCHEADO', M + 20, 13.5);

  doc.setFontSize(5.7);
  doc.setFont('times', 'normal');
  doc.setTextColor(...C.gray);
  txt(doc, 'Sala de Corte · Control de Producción y Trazabilidad', M + 20, 18);

  // Right: client + period
  doc.setFontSize(5.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...C.gray);
  txt(doc, 'CLIENTE', PW - M, 8, { align: 'right' });
  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...C.textDark);
  txt(doc, clienteName || 'Todos los clientes', PW - M, 14, { align: 'right' });

  doc.setFontSize(5.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...C.gray);
  txt(doc, periodo, PW - M, 20, { align: 'right' });
};

// ─── Page Footer (portrait) ───────────────────────────────────────────────────
const drawPageFooter = (doc, pageNum, totalPages) => {
  fillRect(doc, 0, PH - 9, PW, 9, C.offWhite);
  fillRect(doc, 0, PH - 9, PW, 0.4, C.grayBorder);
  fillRect(doc, 0, PH - 0.8, PW, 0.8, C.gold);

  doc.setFontSize(5.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...C.gray);
  txt(doc, 'Las Dos Doncellas · Productos Ibéricos', M, PH - 3.5);
  txt(doc, `Página ${pageNum} / ${totalPages}`, PW - M, PH - 3.5, { align: 'right' });
  doc.setFontSize(5);
  doc.setTextColor(...C.grayLight);
  txt(doc, 'Control de producción y trazabilidad', PW / 2, PH - 3.5, { align: 'center' });
};

// ─── Additional photographic evidence pages ──────────────────────────────────
const drawPhotoContinuationPage = (doc, logo, pieza, clienteName, periodo, images, pageNum, totalPages, startIndex) => {
  fillRect(doc, 0, 0, PW, PH, C.white);
  drawPageHeader(doc, logo, clienteName, periodo, pageNum, totalPages);

  const y0 = 29;
  const usableW = PW - M * 2;
  fillRect(doc, M, y0, usableW, 12, C.textDark);
  fillRect(doc, M, y0, 3, 12, C.gold);

  doc.setFontSize(8.5);
  doc.setFont('times', 'bold');
  doc.setTextColor(...C.white);
  txt(doc, 'EVIDENCIA FOTOGRÁFICA · CONTINUACIÓN', M + 6, y0 + 5);

  doc.setFontSize(5.8);
  doc.setFont('times', 'normal');
  doc.setTextColor(...C.goldLight);
  txt(doc, pieza.producto || 'Pieza de loncheado', M + 6, y0 + 10);
  doc.setTextColor(...C.grayLight);
  txt(doc, pieza.fecha || '', PW - M - 2, y0 + 10, { align: 'right' });

  const gridY = y0 + 16;
  const gridH = PH - gridY - 15;
  const gap = 4;
  const cols = images.length === 1 ? 1 : 2;
  const rows = Math.ceil(images.length / cols);
  const cellW = (usableW - gap * (cols - 1)) / cols;
  const cellH = (gridH - gap * (rows - 1)) / rows;

  images.forEach((imageData, index) => {
    const col = index % cols;
    const row = Math.floor(index / cols);
    const x = M + col * (cellW + gap);
    const y = gridY + row * (cellH + gap);
    fillRect(doc, x - 0.5, y - 0.5, cellW + 1, cellH + 1, C.gold);
    fillRect(doc, x, y, cellW, cellH, C.cardBg2);
    try {
      const props = doc.getImageProperties(imageData);
      const scale = Math.min((cellW - 2) / props.width, (cellH - 7) / props.height);
      const drawW = props.width * scale;
      const drawH = props.height * scale;
      doc.addImage(imageData, 'JPEG', x + (cellW - drawW) / 2, y + 2, drawW, drawH, undefined, 'FAST');
      doc.setFontSize(5);
      doc.setFont('times', 'normal');
      doc.setTextColor(...C.gray);
      txt(doc, `Fotografía ${startIndex + index + 1}`, x + cellW / 2, y + cellH - 2, { align: 'center' });
    } catch {
      doc.setFontSize(6);
      doc.setTextColor(...C.gray);
      txt(doc, `Fotografía ${startIndex + index + 1} no disponible`, x + cellW / 2, y + cellH / 2, { align: 'center' });
    }
  });

  drawPageFooter(doc, pageNum, totalPages);
};

// ═══════════════════════════════════════════════════════════════════════════════
// PIEZA PAGE: One full A4 portrait page per loncheado
// ═══════════════════════════════════════════════════════════════════════════════
const drawPiezaPage = async (doc, logo, pieza, clienteName, periodo, pageNum, totalPages) => {
  fillRect(doc, 0, 0, PW, PH, C.white);
  drawPageHeader(doc, logo, clienteName, periodo, pageNum, totalPages);

  const contentStartY = 26;
  const contentEndY = PH - 11;
  const usableW = PW - M * 2;

  // ── Piece header bar ──────────────────────────────────────────────────────
  fillRect(doc, M, contentStartY, usableW, 12, C.textDark);
  fillRect(doc, M, contentStartY, 3, 12, C.gold);

  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...C.white);
  const prodName = pieza.producto || 'Producto';
  txt(doc, prodName, M + 6, contentStartY + 5);

  doc.setFontSize(6);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...C.goldLight);
  txt(doc, `Cliente: ${pieza.cliente || '-'}`, M + 6, contentStartY + 10);

  // Date + lote on right
  doc.setFontSize(6);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...C.grayLight);
  txt(doc, `Fecha: ${pieza.fecha}`, PW - M - 2, contentStartY + 5, { align: 'right' });
  if (pieza.lote) {
    txt(doc, `Lote: ${pieza.lote}`, PW - M - 2, contentStartY + 10, { align: 'right' });
  }

  // ── Main photo area: compact evidence block, preserving aspect ratio ───────────────────────────────
  const photoY = contentStartY + 14;
  const photoH = Math.round((contentEndY - contentStartY - 14) * 0.40);
  const photoW = usableW;

  if (pieza.imageDataList?.length) {
    // Main technical page shows at most four images. Remaining evidence gets
    // dedicated continuation pages so no photograph is omitted or squeezed.
    const images = pieza.imageDataList.filter(Boolean).slice(0, 4);
    const cols = images.length === 1 ? 1 : 2;
    const rows = Math.ceil(images.length / cols);
    const gap = 3;
    const cellW = (photoW - gap * (cols - 1)) / cols;
    const cellH = (photoH - gap * (rows - 1)) / rows;

    images.forEach((imageData, index) => {
      const col = index % cols;
      const row = Math.floor(index / cols);
      const x = M + col * (cellW + gap);
      const y = photoY + row * (cellH + gap);
      try {
        fillRect(doc, x - 0.6, y - 0.6, cellW + 1.2, cellH + 1.2, C.gold);
        const props = doc.getImageProperties(imageData);
        const scale = Math.min(cellW / props.width, cellH / props.height);
        const drawW = props.width * scale;
        const drawH = props.height * scale;
        doc.addImage(imageData, 'JPEG', x + (cellW - drawW) / 2, y + (cellH - drawH) / 2, drawW, drawH, undefined, 'FAST');
      } catch {
        fillRect(doc, x, y, cellW, cellH, C.cardBg2);
        doc.setFontSize(7);
        doc.setTextColor(...C.gray);
        txt(doc, `Fotografía ${index + 1} no disponible`, x + cellW / 2, y + cellH / 2, { align: 'center' });
      }
    });
  } else {
    fillRect(doc, M, photoY, photoW, photoH, C.cardBg2);
    strokeRect(doc, M, photoY, photoW, photoH, C.grayBorder, 0.5);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...C.gray);
    txt(doc, 'Sin fotografía registrada', PW / 2, photoY + photoH / 2, { align: 'center' });
  }

  // ── Info section below photo ──────────────────────────────────────────────
  const infoY = photoY + photoH + 4;
  const infoH = contentEndY - infoY;

  // Split into left (datos/trazabilidad) and right (responsable + rendimiento)
  const leftW = usableW * 0.55;
  const rightW = usableW - leftW - 4;
  const leftX = M;
  const rightX = M + leftW + 4;

  // ── LEFT: Datos de producción y trazabilidad ───────────────────────────────────────────────────
  fillRect(doc, leftX, infoY, leftW, infoH, C.white);
  strokeRect(doc, leftX, infoY, leftW, infoH, C.grayBorder, 0.3);

  // Section title
  fillRect(doc, leftX, infoY, leftW, 7, C.offWhite);
  fillRect(doc, leftX, infoY, 2.5, 7, C.gold);
  doc.setFontSize(6);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...C.textDark);
  txt(doc, 'DATOS DE PRODUCCIÓN · TRAZABILIDAD', leftX + 5, infoY + 5);

  const fields = [
    { label: 'Producto',          value: pieza.producto || '-' },
    { label: 'Fecha',             value: pieza.fecha || '-' },
    { label: 'Peso Inicial',      value: `${fmt(pieza.pesoInicial, 2)} kg` },
    { label: 'Peso Aprovechado',  value: `${fmt(pieza.pesoAprovechado, 2)} kg` },
    { label: 'Merma',             value: `${fmt(pieza.merma, 2)} kg` },
    { label: 'Rendimiento',       value: `${fmt(pieza.rendimiento, 1)}%`, highlight: true },
    { label: 'Lote interno',      value: pieza.loteInterno || '' },
    { label: 'Lote externo',      value: pieza.loteExterno || '' },
    { label: 'Formato de corte',  value: pieza.formatoCorte || '' },
    { label: 'Nº de blísteres',   value: pieza.numeroBlisteres ?? '' },
    { label: 'Emplatado',         value: pieza.emplatado === true ? 'Sí' : pieza.emplatado === false ? '' : (pieza.emplatado || '') },
  ].filter(f => f.value !== '' && f.value !== null && f.value !== undefined);

  const fieldH = (infoH - 9) / fields.length;
  fields.forEach((f, i) => {
    const fy = infoY + 8 + i * fieldH;

    // Alternating row bg
    if (i % 2 === 0) fillRect(doc, leftX + 0.5, fy - 0.5, leftW - 1, fieldH, C.cardBg);

    // Label
    doc.setFontSize(5.5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...C.gray);
    txt(doc, f.label.toUpperCase(), leftX + 4, fy + fieldH * 0.65);

    // Value
    doc.setFontSize(6.5);
    doc.setFont('helvetica', 'bold');
    if (f.highlight) {
      doc.setTextColor(...rendColor(pieza.rendimiento));
    } else {
      doc.setTextColor(...C.textDark);
    }
    txt(doc, f.value, leftX + leftW - 4, fy + fieldH * 0.65, { align: 'right' });

    // Separator
    doc.setDrawColor(...C.grayBorder);
    doc.setLineWidth(0.15);
    doc.line(leftX + 2, fy + fieldH, leftX + leftW - 2, fy + fieldH);
  });

  // ── RIGHT: Responsable + rendimiento ─────────────────────────────────────
  // Cortador block (top half of right)
  const cortadorBlockH = Math.round(infoH * 0.52);
  fillRect(doc, rightX, infoY, rightW, cortadorBlockH, C.white);
  strokeRect(doc, rightX, infoY, rightW, cortadorBlockH, C.grayBorder, 0.3);

  // Title
  fillRect(doc, rightX, infoY, rightW, 7, C.textDark);
  fillRect(doc, rightX, infoY, 2.5, 7, C.gold);
  doc.setFontSize(6);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...C.white);
  txt(doc, 'RESPONSABLE DEL CORTE', rightX + 5, infoY + 5);

  const cortadorName = pieza.empleado || 'Sin asignar';
  doc.setFontSize(5.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...C.gray);
  txt(doc, 'RESPONSABLE', rightX + 5, infoY + 17);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...C.textDark);
  const cortLines = doc.splitTextToSize(cortadorName, rightW - 10);
  cortLines.slice(0, 2).forEach((line, li) => txt(doc, line, rightX + 5, infoY + 25 + li * 6));
  doc.setFontSize(5.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...C.gray);
  txt(doc, `Fecha de proceso: ${pieza.fecha}`, rightX + 5, infoY + cortadorBlockH - 7);

  // ── Aprovechamiento visual bar ─────────────────────────────────────────────
  const aprovY = infoY + cortadorBlockH + 3;
  const aprovH = infoH - cortadorBlockH - 3;
  fillRect(doc, rightX, aprovY, rightW, aprovH, C.white);
  strokeRect(doc, rightX, aprovY, rightW, aprovH, C.grayBorder, 0.3);

  // Title
  fillRect(doc, rightX, aprovY, rightW, 7, C.offWhite);
  fillRect(doc, rightX, aprovY, 2.5, 7, C.gold);
  doc.setFontSize(6);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...C.textDark);
  txt(doc, 'APROVECHAMIENTO', rightX + 5, aprovY + 5);

  // Rendimiento value big
  const rend = parseFloat(pieza.rendimiento) || 0;
  const mermaP = 100 - rend;

  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...rendColor(rend));
  txt(doc, `${fmt(rend, 1)}%`, rightX + rightW / 2, aprovY + 20, { align: 'center' });

  doc.setFontSize(6);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...C.gray);
  txt(doc, rendLabel(rend).toUpperCase(), rightX + rightW / 2, aprovY + 26, { align: 'center' });

  // Visual bar
  const barX = rightX + 4;
  const barW2 = rightW - 8;
  const barY = aprovY + 30;
  const barH = 7;

  // Background (merma = red)
  fillRect(doc, barX, barY, barW2, barH, C.red);
  // Aprovechado (green)
  const aprovW = Math.max(0, Math.min(barW2, (rend / 100) * barW2));
  fillRect(doc, barX, barY, aprovW, barH, C.green);

  // Bar border
  strokeRect(doc, barX, barY, barW2, barH, C.grayBorder, 0.2);

  // Labels below bar
  doc.setFontSize(5);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...C.green);
  txt(doc, `${fmt(rend, 1)}% Aprovechado`, barX, barY + barH + 4);
  doc.setTextColor(...C.red);
  txt(doc, `${fmt(mermaP, 1)}% Merma`, barX + barW2, barY + barH + 4, { align: 'right' });

  // Observaciones if any
  if (pieza.observaciones) {
    const obsY = aprovY + 46;
    if (obsY < aprovY + aprovH - 4) {
      doc.setFontSize(5);
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(...C.gray);
      const obsLines = doc.splitTextToSize(`Obs: ${pieza.observaciones}`, rightW - 6);
      obsLines.slice(0, 2).forEach((line, li) => {
        txt(doc, line, rightX + 3, obsY + li * 4.5);
      });
    }
  }

  drawPageFooter(doc, pageNum, totalPages);
};

// ═══════════════════════════════════════════════════════════════════════════════
// SUMMARY PAGE: Last page with table of all pieces
// ═══════════════════════════════════════════════════════════════════════════════
const drawSummaryPage = (doc, logo, piezasData, stats, clienteName, periodo, pageNum, totalPages) => {
  fillRect(doc, 0, 0, PW, PH, C.white);
  drawPageHeader(doc, logo, clienteName, periodo, pageNum, totalPages);

  const contentY = 26;
  const usableW = PW - M * 2;

  // ── KPI summary strip ─────────────────────────────────────────────────────
  const kpiH = 18;
  const kpiData = [
    { label: 'Piezas', value: String(stats.totalPiezas), color: C.gold },
    { label: 'Peso Inicial', value: `${fmt(stats.pesoInicial, 1)} kg`, color: C.textDark },
    { label: 'Peso Neto', value: `${fmt(stats.pesoAprovechado, 1)} kg`, color: C.green },
    { label: 'Merma Total', value: `${fmt(stats.mermaTotal, 1)} kg`, color: C.red },
    { label: 'Rendimiento Medio', value: `${fmt(stats.rendimientoMedio, 1)}%`, color: rendColor(stats.rendimientoMedio) },
  ];

  const kpiW = (usableW - 4 * 3) / 5;
  kpiData.forEach((k, i) => {
    const kx = M + i * (kpiW + 3);
    fillRect(doc, kx, contentY, kpiW, kpiH, C.white);
    strokeRect(doc, kx, contentY, kpiW, kpiH, C.grayBorder, 0.3);
    fillRect(doc, kx, contentY, kpiW, 1.5, k.color);

    doc.setFontSize(5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...C.gray);
    txt(doc, k.label.toUpperCase(), kx + kpiW / 2, contentY + 7, { align: 'center' });

    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...k.color);
    txt(doc, k.value, kx + kpiW / 2, contentY + 14, { align: 'center' });
  });

  // ── Section title ─────────────────────────────────────────────────────────
  const tableHeaderY = contentY + kpiH + 5;
  fillRect(doc, M, tableHeaderY, usableW, 7, C.textDark);
  fillRect(doc, M, tableHeaderY, 3, 7, C.gold);
  doc.setFontSize(6.5);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...C.white);
  txt(doc, 'RESUMEN SEMANAL DE PRODUCCIÓN', M + 6, tableHeaderY + 5);

  // ── Summary table ─────────────────────────────────────────────────────────
  const tableRows = piezasData.map((p, idx) => [
    String(idx + 1),
    p.producto || '-',
    p.fecha || '-',
    p.empleado || '-',
    `${fmt(p.pesoInicial, 2)} kg`,
    `${fmt(p.pesoAprovechado, 2)} kg`,
    `${fmt(p.merma, 2)} kg`,
    `${fmt(p.rendimiento, 1)}%`,
  ]);

  autoTable(doc, {
    startY: tableHeaderY + 8,
    head: [['#', 'Producto', 'Fecha', 'Cortador', 'Peso Inicial', 'Peso Aprovechado', 'Merma', 'Rendimiento']],
    body: tableRows,
    margin: { left: M, right: M },
    tableWidth: usableW,
    styles: {
      font: 'helvetica',
      fontSize: 6,
      cellPadding: { top: 2, bottom: 2, left: 2, right: 2 },
      textColor: C.textDark,
      lineColor: C.grayBorder,
      lineWidth: 0.15,
    },
    headStyles: {
      fillColor: C.grayDark,
      textColor: C.white,
      fontStyle: 'bold',
      fontSize: 6,
      halign: 'center',
    },
    columnStyles: {
      0: { halign: 'center', cellWidth: 8 },
      1: { cellWidth: 'auto' },
      2: { halign: 'center', cellWidth: 20 },
      3: { cellWidth: 30 },
      4: { halign: 'right', cellWidth: 22 },
      5: { halign: 'right', cellWidth: 26 },
      6: { halign: 'right', cellWidth: 18 },
      7: { halign: 'center', cellWidth: 22, fontStyle: 'bold' },
    },
    didDrawPage: (data) => {
      // autoTable may paginate. Keep every continuation page visually valid.
      if (data.pageNumber > 1) {
        fillRect(doc, 0, 0, PW, 24, C.white);
        fillRect(doc, 0, 0, PW, 1.5, C.gold);
        doc.setFontSize(7);
        doc.setFont('times', 'bold');
        doc.setTextColor(...C.textDark);
        txt(doc, 'RESUMEN SEMANAL DE PRODUCCIÓN · CONTINUACIÓN', M, 13);
      }
    },
    didParseCell: (data) => {
      if (data.section === 'body') {
        const rend = piezasData[data.row.index]?.rendimiento;
        if (data.column.index === 7) {
          data.cell.styles.textColor = rendColor(rend);
        }
        if (data.row.index % 2 === 0) {
          data.cell.styles.fillColor = C.cardBg;
        }
      }
    },
  });

  // ── Corporate closing ───────────────────────────────────────────────────────
  // Place the closing after the table; if necessary use a clean continuation page.
  const lastTableY = doc.lastAutoTable?.finalY || (tableHeaderY + 20);
  let msgY = Math.max(lastTableY + 7, PH - 30);
  if (msgY + 20 > PH - 10) {
    doc.addPage();
    fillRect(doc, 0, 0, PW, PH, C.white);
    drawPageHeader(doc, logo, clienteName, periodo, pageNum, totalPages);
    msgY = 40;
  }
  fillRect(doc, M, msgY, usableW, 18, C.offWhite);
  strokeRect(doc, M, msgY, usableW, 18, C.grayBorder, 0.3);
  fillRect(doc, M, msgY, 3, 18, C.gold);

  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...C.textDark);
  txt(doc, 'CIERRE DEL PERIODO · CONTROL DE PRODUCCIÓN Y TRAZABILIDAD', PW / 2, msgY + 7, { align: 'center' });

  doc.setFontSize(6);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...C.gray);
  txt(doc, `Cliente: ${clienteName} · Periodo: ${periodo} · ${stats.totalPiezas} piezas procesadas`, PW / 2, msgY + 13, { align: 'center' });

  drawPageFooter(doc, pageNum, totalPages);
};

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN EXPORT FUNCTION
// ═══════════════════════════════════════════════════════════════════════════════
export const exportLoncheadosPDF = async ({
  filteredLoncheados = [],
  clientes = [],
  empleados = [],
  productos = [],
  filters = {},
}) => {
  const logo = await loadLogo();

  // ── Resolve names ──────────────────────────────────────────────────────────
  const getCliente  = (id) => clientes.find(c => String(c.id) === String(id))?.nombre || '-';
  const getEmpleado = (id) => empleados.find(e => String(e.id) === String(id))?.nombre || '-';
  const getProducto = (id) => {
    if (!id) return '-';
    return productos.find(p => String(p.id) === String(id))?.nombre || `Producto ID ${String(id).slice(0, 8)}`;
  };

  // ── Client name ────────────────────────────────────────────────────────────
  const clienteNombre = filters?.clienteId && filters.clienteId !== 'all'
    ? (clientes.find(c => c.id === filters.clienteId)?.nombre || 'Cliente')
    : 'Todos los clientes';

  // ── Date range ─────────────────────────────────────────────────────────────
  let periodo = 'Todos los registros';
  if (filteredLoncheados.length > 0) {
    const dates = filteredLoncheados.map(l => new Date(l.createdAt)).sort((a, b) => a - b);
    const from = fmtDate(dates[0]);
    const to   = fmtDate(dates[dates.length - 1]);
    periodo = from === to ? from : `${from} – ${to}`;
  }

  // ── Compute global stats ───────────────────────────────────────────────────
  const totalPiezas     = filteredLoncheados.length;
  const pesoInicial     = filteredLoncheados.reduce((s, l) => s + (parseFloat(l.pesoInicial) || 0), 0);
  const pesoAprovechado = filteredLoncheados.reduce((s, l) => s + (parseFloat(l.pesoAprovechado) || 0), 0);
  const mermaTotal      = filteredLoncheados.reduce((s, l) => s + (parseFloat(l.merma) || 0), 0);
  const rendValues      = filteredLoncheados.map(l => parseFloat(l.rendimiento)).filter(v => !isNaN(v) && v > 0);
  const rendimientoMedio = rendValues.length > 0 ? rendValues.reduce((a, b) => a + b, 0) / rendValues.length : 0;

  const stats = {
    totalPiezas, pesoInicial, pesoAprovechado, mermaTotal, rendimientoMedio,
  };

  // ── Prepare piece data with photos ────────────────────────────────────────
  const piezasData = await Promise.all(
    filteredLoncheados
      .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
      .map(async (l) => ({
        producto:        l.productoNombre || l.producto?.nombre || getProducto(l.productoId || l.producto_id),
        cliente:         l.clienteNombre || getCliente(l.clienteId),
        empleado:        l.empleadoNombre || getEmpleado(l.empleadoId),
        fecha:           fmtDate(l.createdAt),
        pesoInicial:     l.pesoInicial,
        pesoAprovechado: l.pesoAprovechado,
        merma:           l.merma,
        rendimiento:     l.rendimiento,
        lote:            l.loteInterno || l.lote_interno || l.loteExterno || l.lote_externo || '',
        categoria:       l.categoria || '',
        id:               l.id,
        loteInterno:      l.loteInterno || l.lote_interno || '',
        loteExterno:      l.loteExterno || l.lote_externo || '',
        observaciones:   l.observaciones || '',
        formatoCorte:     l.formatoCorte || l.formato_corte || '',
        numeroBlisteres:  l.numeroBlisteres ?? l.numero_blisteres ?? '',
        emplatado:        l.emplatado,
        imageDataList:   (await Promise.all(
          normalizeFotoUrls(l.fotos?.length ? l.fotos : (l.fotoUrl || l.foto_url))
            .map(url => loadImageFromUrl(url))
        )).filter(Boolean),
      }))
  );

  // ── Total pages: technical page + photo continuations + summary ──────────
  const continuationPages = piezasData.reduce(
    (sum, pieza) => sum + Math.ceil(Math.max(0, (pieza.imageDataList?.length || 0) - 4) / 4),
    0
  );
  const totalPages = piezasData.length + continuationPages + 1;

  // ── Build PDF (A4 Portrait) ───────────────────────────────────────────────
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

  // One technical page per piece, followed by as many photo pages as required.
  let pageNum = 1;
  for (let i = 0; i < piezasData.length; i++) {
    if (pageNum > 1) doc.addPage();
    await drawPiezaPage(doc, logo, piezasData[i], clienteNombre, periodo, pageNum, totalPages);
    pageNum += 1;

    const extraImages = (piezasData[i].imageDataList || []).slice(4);
    for (let offset = 0; offset < extraImages.length; offset += 4) {
      doc.addPage();
      const chunk = extraImages.slice(offset, offset + 4);
      drawPhotoContinuationPage(doc, logo, piezasData[i], clienteNombre, periodo, chunk, pageNum, totalPages, 4 + offset);
      pageNum += 1;
    }
  }

  // Summary page (last)
  if (piezasData.length > 0) {
    doc.addPage();
    drawSummaryPage(doc, logo, piezasData, stats, clienteNombre, periodo, pageNum, totalPages);
  } else {
    // No data fallback
    fillRect(doc, 0, 0, PW, PH, C.white);
    drawPageHeader(doc, logo, clienteNombre, periodo, 1, 1);
    doc.setFontSize(10);
    doc.setTextColor(...C.gray);
    txt(doc, 'No hay piezas registradas para este periodo.', PW / 2, PH / 2, { align: 'center' });
    drawPageFooter(doc, 1, 1);
  }

  // ── Filename ───────────────────────────────────────────────────────────────
  const weekNum = getWeekNumber(new Date());
  const year    = new Date().getFullYear();
  const safeCliente = clienteNombre.replace(/[^a-zA-Z0-9áéíóúÁÉÍÓÚñÑ]/g, '_').substring(0, 30);
  const filename = `Informe_Loncheado_${safeCliente}_Semana${weekNum}_${year}.pdf`;

  doc.save(filename);
};
